// The stadium-screen token is the only credential a screen has, and it sits
// in the URL. Analytics and error tracking record URLs, so every string they
// send is scrubbed of the token segment first.

const TOKEN_SEGMENT = /(\/screen\/)[^/?#\s]+/g;

/** `/screen/<token>/motm` → `/screen/[redacted]/motm`; other strings unchanged. */
export function redactScreenToken(value: string): string {
  return value.replace(TOKEN_SEGMENT, '$1[redacted]');
}

/** Only plain objects and arrays are walked; anything built by a class is not ours to rebuild. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object') return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Redacts every string inside an event payload (plain objects and arrays, any
 * depth). Two things it must never do, both seen in production (23 Oct 2026):
 *  - rebuild a class instance: PostHog's `timestamp: Date` became `{}` and the
 *    ingestion API refused every $pageview with a 400;
 *  - follow a loop: Sentry event data (and DOM nodes in breadcrumbs) can point
 *    back at themselves, and the walk overflowed the stack on every page load.
 * So class instances (Date, Error, Map, DOM nodes, …) are returned as they
 * are, and each object is copied once: a reference back to it gets the copy.
 */
export function redactScreenTokensDeep<T>(value: T, seen: WeakMap<object, unknown> = new WeakMap()): T {
  if (typeof value === 'string') return redactScreenToken(value) as unknown as T;
  if (Array.isArray(value)) {
    if (seen.has(value)) return seen.get(value) as T;
    const out: unknown[] = [];
    seen.set(value, out);
    for (const v of value) out.push(redactScreenTokensDeep(v, seen));
    return out as unknown as T;
  }
  if (isPlainObject(value)) {
    if (seen.has(value)) return seen.get(value) as T;
    const out: Record<string, unknown> = {};
    seen.set(value, out);
    for (const [k, v] of Object.entries(value)) out[k] = redactScreenTokensDeep(v, seen);
    return out as T;
  }
  return value;
}
